import React, { useState } from 'react';
import { css } from 'astroturf';

const styles = css`
  /* Variables based on Polypane's aesthetic */
  .container {
    --pp-bg: #0f1115;
    --pp-card-bg: #181b21;
    --pp-card-border: #2d313a;
    --pp-text-main: #ffffff;
    --pp-text-muted: #9ca3af;
    --pp-accent-gradient: linear-gradient(135deg, #0096ff 0%, #8f41e9 100%);
    --pp-accent-blue: #0096ff;
    --pp-accent-hover: #33adff;

    background-color: var(--pp-bg);
    color: var(--pp-text-main);
    padding: 4rem 1.5rem;
    font-family: 'Inter', system-ui, sans-serif;
  }

  .wrapper {
    max-width: 1000px;
    margin: 0 auto;
  }

  /* Header Section */
  .header {
    text-align: center;
    margin-bottom: 3rem;
  }

  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--pp-accent-blue);
    margin-bottom: 0.5rem;
    display: block;
  }

  .heading {
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    line-height: 1.2;
  }

  .subheading {
    color: var(--pp-text-muted);
    font-size: 1.1rem;
  }

  /* Grid Layout */
  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (min-width: 768px) {
    .grid {
      grid-template-columns: 1fr 1.2fr;
      gap: 3rem;
      align-items: center;
    }
  }

  /* Left Column: Buttons */
  .optionsColumn {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .optionButton {
    background: var(--pp-card-bg);
    border: 1px solid var(--pp-card-border);
    border-radius: 12px;
    padding: 1.5rem;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
    color: var(--pp-text-muted);
  }

  .optionButton:hover {
    border-color: var(--pp-text-muted);
    background: #1f232b;
  }

  .optionButton.active {
    border-color: var(--pp-accent-blue);
    background: #1f232b;
    color: var(--pp-text-main);
    box-shadow: 0 0 0 1px var(--pp-accent-blue);
  }

  .triggerText {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    opacity: 0.7;
    margin-bottom: 0.25rem;
  }

  .labelText {
    display: block;
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.3;
  }

  .arrow {
    position: absolute;
    right: 1.5rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--pp-accent-blue);
    font-weight: bold;
    animation: slideRight 0.3s ease-out;
  }

  /* Right Column: Solution */
  .solutionColumn {
    min-height: 350px;
    position: relative;
  }

  .solutionCard {
    background: var(--pp-card-bg);
    border: 1px solid var(--pp-card-border);
    border-radius: 16px;
    padding: 2.5rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.5);
  }

  .iconBadge {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    background: rgba(255, 255, 255, 0.1);
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }

  .solutionTitle {
    font-size: 1.75rem;
    margin: 0 0 1rem 0;
    background: var(--pp-accent-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .solutionBody {
    color: var(--pp-text-muted);
    line-height: 1.6;
    margin-bottom: 2rem;
    font-size: 1.05rem;
  }

  .primaryBtn {
    display: inline-block;
    background: var(--pp-accent-blue);
    color: white;
    font-weight: 600;
    padding: 0.85rem 1.5rem;
    border-radius: 8px;
    text-decoration: none;
    transition: background 0.2s;
    text-align: center;
  }

  .primaryBtn:hover {
    background: var(--pp-accent-hover);
  }

  .trialNote {
    display: block;
    margin-top: 0.75rem;
    font-size: 0.8rem;
    color: var(--pp-text-muted);
    opacity: 0.7;
  }

  /* Empty State */
  .emptyState {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    border: 2px dashed var(--pp-card-border);
    border-radius: 16px;
    padding: 2rem;
    color: var(--pp-text-muted);
  }

  .emptyCircle {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: var(--pp-card-border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  /* Animations */
  .animateIn {
    animation: fadeIn 0.4s ease-out forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideRight {
    from {
      opacity: 0;
      transform: translate(-10px, -50%);
    }
    to {
      opacity: 1;
      transform: translate(0, -50%);
    }
  }
`;

const PAIN_POINTS = [
  {
    id: 'responsive',
    label: ' constantly resizing my browser window',
    triggerText: 'I waste time...',
    solutionTitle: 'Stop the resize dance.',
    solutionBody:
      'Polypane shows you mobile, tablet, and 5K desktop views side-by-side. They scroll, click, and hover in perfect sync.',
    cta: 'See your site on all screens',
    icon: '📱',
  },
  {
    id: 'a11y',
    label: 'missing accessibility bugs until it’s too late',
    triggerText: 'I worry about...',
    solutionTitle: 'Audit while you build.',
    solutionBody:
      'Don’t wait for a separate audit. Get live feedback on contrast, structure, and focus order right in the browser as you code.',
    cta: 'Find a11y bugs instantly',
    icon: '👁️',
  },
  {
    id: 'social',
    label: 'guessing how links look on Twitter/Slack',
    triggerText: 'I get frustrated...',
    solutionTitle: 'Pixel-perfect previews.',
    solutionBody:
      'Preview your meta tags for Twitter, LinkedIn, Slack, and Google Search. No more deployment just to check a social card.',
    cta: 'Preview social cards',
    icon: '🐦',
  },
];

const WorkflowDiagnostic = () => {
  const [activeId, setActiveId] = useState(null);

  const activeContent = PAIN_POINTS.find((p) => p.id === activeId);

  return (
    <section className={styles.container}>
      <div className={styles.wrapper}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.eyebrow}>Workflow Diagnostic</span>
          <h2 className={styles.heading}>What is your biggest time sink?</h2>
          <p className={styles.subheading}>Select your bottleneck to see how we fix it.</p>
        </div>

        {/* Interaction Grid */}
        <div className={styles.grid}>
          {/* Left Column: The Commitment (Questions) */}
          <div className={styles.optionsColumn}>
            {PAIN_POINTS.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveId(item.id)}
                className={`${styles.optionButton} ${activeId === item.id ? styles.active : ''}`}
                aria-pressed={activeId === item.id}
              >
                <span className={styles.triggerText}>{item.triggerText}</span>
                <span className={styles.labelText}>{item.label}</span>
                {activeId === item.id && <div className={styles.arrow}>→</div>}
              </button>
            ))}
          </div>

          {/* Right Column: The Reward (Solution) */}
          <div className={styles.solutionColumn}>
            {activeContent ? (
              <div className={`${styles.solutionCard} ${styles.animateIn}`}>
                <div className={styles.iconBadge}>{activeContent.icon}</div>
                <h3 className={styles.solutionTitle}>{activeContent.solutionTitle}</h3>
                <p className={styles.solutionBody}>{activeContent.solutionBody}</p>

                <div className={styles.ctaWrapper}>
                  <a href="/download" className={styles.primaryBtn}>
                    {activeContent.cta}
                  </a>
                  <span className={styles.trialNote}>14-day free trial • No CC required</span>
                </div>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyCircle}>?</div>
                <p>Select a challenge on the left to reveal your workflow upgrade.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowDiagnostic;
